/**
 * Turn any thrown value from an API call into ONE clean, user-facing message.
 *
 * The rule (per product requirement): never surface the raw network / axios /
 * server / DB response to the user. We only echo the backend's own `message`
 * when it's an intentional 4xx validation / business-rule string that reads
 * like something a human wrote. Everything else — 5xx, transport failures,
 * timeouts, stack traces, driver/ORM errors — collapses to a friendly generic.
 *
 * Usage:
 *   toast.push(apiError(e, 'Could not save GRN'), 'error');
 */

const MSG_NETWORK = "Can't reach the server. Check your network and try again.";
const MSG_TIMEOUT = 'The request timed out. Please try again.';
const MSG_SERVER = 'Something went wrong on the server. Please try again in a moment.';
const MSG_AUTH = 'Your session has expired. Please sign in again.';
const MSG_FORBIDDEN = "You don't have permission to do that.";
const MSG_DEFAULT = 'Something went wrong. Please try again.';

/** True only when a backend message is safe to show a user verbatim. */
function looksHuman(raw: string): boolean {
  const s = raw.trim();
  if (!s) return false;
  if (s.length > 240) return false;
  // Multi-line output or a stack trace — never a user message.
  if (s.split('\n').length > 3 || /\n\s*at\s/.test(s)) return false;
  // Raw driver / ORM / runtime error text leaking through.
  if (
    /\b(prisma|econnrefused|econnreset|etimedout|enotfound|econnaborted|sqlstate|mssql|tedious|referenceerror|typeerror|syntaxerror)\b/i.test(s) ||
    /cannot read propert|is not a function|is not defined|undefined is not|null is not/i.test(s)
  ) {
    return false;
  }
  // The generic axios line — not for users.
  if (/^request failed with status code/i.test(s)) return false;
  return true;
}

/** Pull `message` out of a backend error body, joining validation arrays. */
function pickMessage(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const m = (data as { message?: unknown }).message;
  if (Array.isArray(m)) {
    const joined = m.filter((x): x is string => typeof x === 'string').join('; ');
    return joined || undefined;
  }
  return typeof m === 'string' ? m : undefined;
}

interface AxiosLike {
  isAxiosError?: boolean;
  code?: string;
  message?: string;
  request?: unknown;
  response?: { status?: number; data?: unknown };
}

export function apiError(e: unknown, fallback: string = MSG_DEFAULT): string {
  if (!e || typeof e !== 'object') return fallback;
  const ax = e as AxiosLike;

  const isAxios = ax.isAxiosError || 'response' in ax || 'request' in ax || 'code' in ax;
  if (!isAxios) return fallback;

  // Transport failure — the request never got a response back.
  if (!ax.response) {
    if (ax.code === 'ECONNABORTED' || /timeout/i.test(ax.message ?? '')) return MSG_TIMEOUT;
    return MSG_NETWORK;
  }

  const status = ax.response.status ?? 0;
  if (status === 401) return MSG_AUTH;
  if (status === 403) return MSG_FORBIDDEN;
  if (status >= 500) return MSG_SERVER;

  // 4xx — our API's intentional messages. Show only if they read cleanly.
  const msg = pickMessage(ax.response.data);
  if (msg && looksHuman(msg)) return msg;
  return fallback;
}
